import { isNil, isNotNil } from '@flowio/is';
import { showLoadingSpinner, hideLoadingSpinner } from '../../utilities/loadingSpinner';
import type { DomReference, PaymentButtons } from '../../types';
import loadScript from '../../utilities/loadScript';
import { resolveElement, resolveButton } from '../../utilities/elementHelpers';
import {
  removeKlarnaChoiceUI,
  removeKlarnaContinueButton,
  renderKlarnaChoiceUI,
  renderKlarnaContinueButton,
} from './ui-helpers';
import postPaymentMethodData from '../../services/paymentRequest/postPaymentMethodData';
import injectKlarnaCss from './ui-css';
import injectCommonCss from '../../utilities/commonStyles';

const KLARNA_SDK = 'https://x.klarnacdn.net/kp/lib/v1/api.js';

function isKlarnaV1Action(
  action: io.flow.v0.unions.Action,
): action is io.flow.v0.models.ActionUseSdkKlarnaV1 {
  return action.type === 'use_sdk_klarna_v1';
}

type HandleKlarnaV1ActionParams = {
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
  action: io.flow.v0.models.ActionUseSdkKlarnaV1,
  container: DomReference,
  buttons?: PaymentButtons,
};

function isKlarnaAuthorizationResponse(
  response: KlarnaSDK.AuthorizeResponse | KlarnaSDK.FinalizeResponse,
): response is KlarnaSDK.AuthorizeResponse {
  return isNotNil((response as KlarnaSDK.AuthorizeResponse).finalize_required);
}

export default function handleKlarnaV1Action(
  params: HandleKlarnaV1ActionParams,
): Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  if (!isKlarnaV1Action(params.action)) {
    throw new Error('handleKlarnaV1Action: incorrect \'action\' parameter type.');
  }

  const resolvedContainer = resolveElement(params.container);
  const resolvedButton = resolveButton(params.buttons, 'primary');

  if (isNil(resolvedContainer)) {
    // eslint-disable-next-line no-console
    console.error('handleKlarnaV1Action: Mounting container was not set.');
    return Promise.reject(new Error('handleKlarnaV1Action: Mounting container was not set.'));
  }

  return new Promise<
  io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest
  >((resolve, reject) => {
    showLoadingSpinner();
    injectCommonCss(['radio', 'paymentButton']);
    injectKlarnaCss();

    const handleKlarnaResponse = (
      category: string,
    ) => (
      response: KlarnaSDK.AuthorizeResponse | KlarnaSDK.FinalizeResponse,
    ) => {
      // If Klarna instructs, we should remove the UI and show the loading spinner.
      if (!response.show_form) {
        removeKlarnaChoiceUI(resolvedContainer);
        removeKlarnaContinueButton(resolvedButton);
        showLoadingSpinner();
      }

      if (response.approved) {
        if (response.authorization_token) {
          // if approved and we have an authorization token, we can remove the UI.
          removeKlarnaChoiceUI(resolvedContainer);
          removeKlarnaContinueButton(resolvedButton);
          postPaymentMethodData(params.pr, {
            // TODO: fix form when we have an API spec
            type: 'authorize_klarna',
            result: {
              type: 'success',
              authorization_token: response.authorization_token,
            },
          }).then((resolvedPr) => {
            resolve(resolvedPr);
          }).catch((error: unknown) => {
            reject(new Error(`handleKlarnaV1Action: exception raised while recording Klarna success: ${String(error)}`));
          });
        } else if (isKlarnaAuthorizationResponse(response) && response.finalize_required) {
          try {
            Klarna.Payments.finalize({
              payment_method_category: category,
            }, handleKlarnaResponse(category));
          } catch (error) {
            reject(new Error(`handleKlarnaV1Action: exception raised while finalizing Klarna payment: ${String(error)}`));
          }
        } else {
          reject(new Error('handleKlarnaV1Action: authorization was approved but required parameters were not specified'));
        }
      } else {
        postPaymentMethodData(params.pr, {
          // TODO: fix form when we have an API spec
          type: 'authorize_klarna',
          result: {
            type: 'failure',
          },
        }).then((resolvedPr) => {
          resolve(resolvedPr);
        }).catch((error: unknown) => {
          reject(new Error(`handleKlarnaV1Action: exception raised while recording Klarna failure: ${String(error)}`));
        });
      }
    };

    loadScript(KLARNA_SDK).then(() => {
      try {
        Klarna.Payments.init({
          client_token: params.action.client_token,
        });
      } catch (e) {
        throw new Error('handleKlarnaV1Action: Klarna init error');
      }
      renderKlarnaChoiceUI(resolvedContainer, params.action);
      renderKlarnaContinueButton(resolvedButton, handleKlarnaResponse);

      hideLoadingSpinner();
    });
  });
}
